import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Cms } from '@impact/data';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { first } from 'rxjs/operators';

import { SettingsService } from '../core/settings.service';
import { TrackingService } from '../utils/tracking/tracking.service';

@Injectable({
    providedIn: 'root',
})
export class MapsService implements OnDestroy {
    private readonly unsubscribe: Subject<void>;

    public readonly isMapScriptsReady: Promise<boolean>;
    private isScriptsLoaded = false;
    private initResolver?: (gmapsActive: boolean) => void;

    constructor(private http: HttpClient, private trackingService: TrackingService, private settingsService: SettingsService) {
        this.unsubscribe = new Subject<void>();
        this.isMapScriptsReady = new Promise<boolean>((resolve) => {
            this.initResolver = resolve;
        });
        this.isMapScriptsReady.catch(() => {
            console.warn('Something went wrong with the maps script');
        });
    }

    getDistributors(): Observable<any> {
        return this.http.get('api/distributor');
    }

    getGeoCoords(options = {}): Promise<any> {
        return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, options);
        });
    }

    async initGoogleMapsScripts() {
        if (!this.isScriptsLoaded) {
            const settings: Cms.SettingsDto = await firstValueFrom(this.settingsService.get());
            const apiKey = settings.mapsApiKey;
            if (!apiKey) {
                console.warn('Google maps API Key not found');
                return;
            }
            const mapsApiScript = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry`;
            const scriptsArr = [mapsApiScript];
            scriptsArr.forEach((url, index) => {
                this.trackingService.loadScript(url, true, () => {
                    index++;
                    if (scriptsArr.length === index) {
                        this.resolvePromise(true);
                        this.isScriptsLoaded = true;
                    }
                });
            });
        }
    }

    /**
     * Resolves the initPromise. Deferred pushes wait for this.
     * @param {boolean} status - Init successful or not.
     * @param {string} message - Optional warning message for console.
     */
    private resolvePromise(status: boolean, message?: string) {
        if (this.initResolver) {
            this.initResolver(status);
            if (message) {
                console.warn(message);
            }
        }
    }

    public ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
